/**
 * What the insurer asked for when a policy was paid (the Reliance CDT amount, KSHEMA's CD Balance premium) beside what we calculated and debited —
 * for the View page and the print PDF. All money here is whole paise (integers); nothing is summed as a float.
 *
 * Where the amount lives:
 *   policy.insurerPayable.<part>  { insurer, method, amountPaise, amount, proposalNumber, jobId, at }   (new, written by the
 *                                 automation right before Submit Details; part = "motor" | "od" | "tp")
 *   policy.walletPayment / walletPayment_od / walletPayment_tp   the older write-ahead note of the same payment
 *                                 ({ state, amountPaise, proposalNumber, at }) — read for policies paid before the new field
 *                                 existed. A note names its insurer and method when it is KSHEMA's (kshemaWalletIssue.js,
                                 2026-10-22: insurer KSHEMA, method "CD Balance"); one without them is Reliance's CDT.
 */
const PARTS = ["motor", "od", "tp"];
// A note in one of these states means the payment was pressed (or may have been) — the amount was really asked for.
const NOTE_STATES = ["submit-pressed", "issued", "unconfirmed"];

const norm = (v) => String(v ?? "").trim().toLowerCase();

/** 1234.56 (rupees, as the policy stores them) -> 123456; null when it is not a number. */
export const toPaise = (rupees) => {
  if (rupees === null || rupees === undefined || rupees === "") return null;
  const n = Number(rupees);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

/** 123456 -> "₹1,234.56" (always 2 decimals, Indian grouping); "-" when it is not whole paise. */
export const formatPaise = (paise) => {
  if (!Number.isInteger(paise)) return "-";
  const sign = paise < 0 ? "-" : "";
  const abs = Math.abs(paise);
  const rupees = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, "0");
  return `${sign}₹${rupees.toLocaleString("en-IN")}.${frac}`;
};

const noteFieldOf = (part) => (part === "motor" ? "walletPayment" : `walletPayment_${part}`);

/**
 * The amounts the insurer asked for on this policy, one per part that has one: [{ part, insurer, amountPaise, proposalNumber,
 * at, source: "field" | "note" }] in the order motor, od, tp.
 */
export const cdtPayablesOf = (policy) => {
  const out = [];
  for (const part of PARTS) {
    const own = policy?.insurerPayable?.[part];
    if (own && Number.isInteger(own.amountPaise) && own.amountPaise > 0) {
      out.push({
        part,
        insurer: own.insurer || "Reliance",
        method: own.method || "CDT",
        amountPaise: own.amountPaise,
        proposalNumber: own.proposalNumber || "",
        at: own.at || null,
        source: "field",
      });
      continue;
    }
    const note = policy?.[noteFieldOf(part)];
    if (note && NOTE_STATES.includes(note.state) && Number.isInteger(note.amountPaise) && note.amountPaise > 0) {
      out.push({
        part,
        insurer: note.insurer || "Reliance",
        method: note.method || "CDT",
        amountPaise: note.amountPaise,
        proposalNumber: note.proposalNumber || "",
        at: note.at || null,
        source: "note",
      });
    }
  }
  return out;
};

/**
 * What we calculated for that insurer's part — the amount its wallet was debited.
 *
 * The policy does not keep each insurer's wallet charge (the model drops `charge`), so it is worked out from the saved legs
 * and PROVEN against the one authoritative number, walletDeductionAmount (the sum actually debited): the legs' NET adds up to
 * it (new split policies are debited their net) or their TOTAL does (older ones, with GST). Whichever basis reconciles to the
 * paisa is used for the insurer's own legs; when neither does the answer is null ("-"), never a guess. A Brisk leg is left out
 * of the sum when that is what makes it reconcile (the PA certificate is Brisk's, not the insurer's).
 * A policy with no saved legs and one insurer (older) uses its wallet debit — unless a Brisk PA was part of it.
 */
export const calculatedPaiseFor = (policy, insurer) => {
  const debited = toPaise(policy?.walletDeductionAmount);
  const legs = Array.isArray(policy?.premiumCalculation?.split?.legs) ? policy.premiumCalculation.split.legs : [];
  if (legs.length === 0) {
    const briskPa = !!policy?.paCover && norm(policy?.paCoverCompany) === "brisk";
    return !policy?.settings?.isMultipleCompany && !briskPa ? debited : null;
  }
  if (debited === null) return null;
  const sum = (list, field) => list.reduce((acc, l) => acc + (toPaise(l[field]) ?? NaN), 0);
  const sets = [legs, legs.filter((l) => norm(l.company) !== "brisk")];
  for (const set of sets) {
    for (const field of ["net", "total"]) {
      if (sum(set, field) !== debited) continue;
      const own = set.filter((l) => norm(l.company) === norm(insurer));
      if (own.length === 0) return null;
      const paise = sum(own, field);
      return Number.isInteger(paise) ? paise : null;
    }
  }
  return null;
};

/** insurer - calculated, in paise (positive = the insurer asked for more); null when either is missing. */
export const differencePaise = (insurerPaise, calculatedPaise) =>
  Number.isInteger(insurerPaise) && Number.isInteger(calculatedPaise) ? insurerPaise - calculatedPaise : null;

/** "Same" / "₹120.00 more at Reliance" / "₹120.00 less at Reliance" / "-". */
export const differenceText = (diff, insurer = "Reliance") => {
  if (diff === null) return "-";
  if (diff === 0) return "Same";
  return `${formatPaise(Math.abs(diff))} ${diff > 0 ? "more" : "less"} at ${insurer}`;
};

/** The label prefix of a part's rows: "Reliance" for the whole policy, "Reliance OD" / "Reliance TP" for a split one. */
export const payableLabel = (entry) =>
  entry.part === "od" ? `${entry.insurer} OD` : entry.part === "tp" ? `${entry.insurer} TP` : entry.insurer;

/** Everything the View page and the PDF print: one object per part with an amount. */
export const payableRowsOf = (policy) =>
  cdtPayablesOf(policy).map((entry) => {
    const calculated = calculatedPaiseFor(policy, entry.insurer);
    const diff = differencePaise(entry.amountPaise, calculated);
    return {
      ...entry,
      label: payableLabel(entry),
      calculatedPaise: calculated,
      differencePaise: diff,
      calculatedText: calculated === null ? "-" : formatPaise(calculated),
      cdtText: formatPaise(entry.amountPaise),
      // "CDT amount" (Reliance) / "CD Balance amount" (KSHEMA) - what the insurer's own payment calls it
      amountLabel: `${entry.method || "CDT"} amount`,
      differenceText: differenceText(diff, entry.insurer),
    };
  });
